import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Application } from './interfaces';
import {
  asyncGetUserApplications,
  asyncGetApplicationGroups,
  asyncUpdateApplicationStatus,
  asyncGetAppGroupPlaidResults,
  asyncGetApplicationGroupScreening,
  asyncGetAppGroupScreeningRequestData,
  asyncGetApplicationsByGroup,
  asyncRequestApplicationGroupScreening,
  asyncUpdateAppGroup,
  asyncUpdateAppData,
  asyncGetAllPropertyUnits,
  asyncGetPropertyUnitRent,
  asyncUpdateAppInfoService,
  asyncSaveLeaseDraft,
  asyncGetTransactionCategories,
  asyncGetLeasePreview,
  asyncGetApplicationGroupScreeningFromTU,
  asyncgetPropertyFeeRecord,
  asyncGetAdverseActionPreview,
  asyncSendAdverseActionEmails,
  asyncGetPropertyUnits,
} from './easyapp-async';

interface EasyAppState {
  applications: Application[];
  appGroups: any[];
  selectedAppGroupId: any;
  selectedAppGroup: any;
  selectedAppGroupPlaidResults: any;
  selectedAppGroupScreeningReqData: any;
  selectedAppGroupScreeningReqResult: any;
  selectedAppGroupScreening: any;
  selectedPropertyUnits: any;
  selectedPropertyUnitRent: any;
  selectedPropertyFeeRecord: any;
  transactionCategories: any;
  leasePreview: any;
  isFetching: boolean;
  error: string | null;
  adverseActionPdfs: any;
}

const initialState: EasyAppState = {
  applications: [],
  appGroups: [],
  selectedAppGroupId: null,
  selectedAppGroup: null,
  selectedAppGroupPlaidResults: null,
  selectedAppGroupScreeningReqData: null,
  selectedAppGroupScreeningReqResult: null,
  selectedAppGroupScreening: null,
  selectedPropertyUnits: null,
  selectedPropertyUnitRent: null,
  selectedPropertyFeeRecord: null,
  transactionCategories: null,
  leasePreview: null,
  isFetching: false,
  error: null,
  adverseActionPdfs: null,
};

export const easyapp = createSlice({
  name: 'EasyApp',
  initialState,
  reducers: {
    setSelectedAppGroupId(state, action: PayloadAction<string>) {
      state.selectedAppGroupId = action.payload;
    },
    clearGroupData(state) {
      state.selectedAppGroupId = null;
      state.selectedAppGroup = null;
      state.selectedAppGroupPlaidResults = null;
      state.selectedAppGroupScreeningReqData = null;
      state.selectedAppGroupScreeningReqResult = null;
      state.selectedAppGroupScreening = null;
      state.selectedPropertyUnits = null;
      state.selectedPropertyUnitRent = null;
      state.selectedPropertyFeeRecord = null;
      state.transactionCategories = null;
      state.leasePreview = null;
      state.adverseActionPdfs = null;
      state.error = null;
    },
    clearError(state) {
      state.error = null;
    },
    resetEasyAppState() {
      return initialState;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(asyncGetUserApplications.pending, (state) => {
        state.isFetching = true;
        state.error = null;
      })
      .addCase(asyncGetUserApplications.fulfilled, (state, action) => {
        if (!action.payload) return;
        state.applications = action.payload.data;
        state.isFetching = false;
        state.error = null;
      })
      .addCase(asyncGetUserApplications.rejected, (state, action) => {
        state.isFetching = false;
        state.error =
          action.error.message || 'Failed to fetch user applications';
      })
      .addCase(asyncGetApplicationGroups.fulfilled, (state, action) => {
        state.appGroups = action.payload;
      })
      .addCase(asyncUpdateApplicationStatus.fulfilled, (state, action) => {
        const { application_group, status } = action.payload;
        state.applications = state.applications.map((application) =>
          application.application_group === application_group
            ? { ...application, status }
            : application
        );
      })
      .addCase(asyncUpdateAppGroup.fulfilled, (state, action) => {
        const { fieldName, value, application_group } = action.payload;
        state.appGroups = state.appGroups.map((application) =>
          application.application_group === application_group
            ? { ...application, [fieldName]: value }
            : application
        );
      })
      .addCase(asyncUpdateAppData.fulfilled, (state, action) => {
        const { fieldName, value, applicationId } = action.payload;

        if (fieldName === 'email') {
          state.appGroups = state.appGroups.map((group) => {
            const updatedApplicants = group.applicant_names.map((a: any) =>
              a.application_id === applicationId ? { ...a, [fieldName]: value } : a
            );
            return { ...group, applicant_names: updatedApplicants };
          });
        }

        if (!state.selectedAppGroup) return;

        if (state.selectedAppGroup.applicants) {
          const applicantIndex = state.selectedAppGroup.applicants.findIndex(
            (applicant: any) => applicant.application_id === applicationId
          );
          if (applicantIndex !== -1) {
            state.selectedAppGroup.applicants[applicantIndex] = {
              ...state.selectedAppGroup.applicants[applicantIndex],
              [fieldName]: value,
            };
          }
        }

        if (
          (fieldName === 'first_name' || fieldName === 'last_name') &&
          state.selectedAppGroup.applicant_names
        ) {
          const nameIndex = state.selectedAppGroup.applicant_names.findIndex(
            (nameObj: any) => nameObj.application_id === applicationId
          );
          if (nameIndex !== -1) {
            state.selectedAppGroup.applicant_names[nameIndex] = {
              ...state.selectedAppGroup.applicant_names[nameIndex],
              [fieldName]: value,
            };
          }
        }
      })
      .addCase(asyncGetApplicationsByGroup.pending, (state) => {
        state.isFetching = true;
        state.error = null;
      })
      .addCase(asyncGetApplicationsByGroup.fulfilled, (state, action) => {
        state.selectedAppGroup = action.payload;
        state.isFetching = false;
        state.error = null;
      })
      .addCase(asyncGetApplicationsByGroup.rejected, (state, action) => {
        state.isFetching = false;
        state.error =
          action.error.message || 'Failed to fetch applications by group';
      })
      .addCase(asyncGetAppGroupPlaidResults.pending, (state) => {
        state.isFetching = true;
      })
      .addCase(asyncGetAppGroupPlaidResults.fulfilled, (state, action) => {
        state.selectedAppGroupPlaidResults = action.payload;
        state.isFetching = false;
      })
      .addCase(asyncGetAppGroupPlaidResults.rejected, (state, action) => {
        state.isFetching = false;
        state.error =
          action.error.message || 'Failed to fetch Plaid results';
      })
      .addCase(
        asyncGetAppGroupScreeningRequestData.fulfilled,
        (state, action) => {
          state.selectedAppGroupScreeningReqData = action.payload;
        }
      )
      .addCase(
        asyncRequestApplicationGroupScreening.fulfilled,
        (state, action) => {
          state.selectedAppGroupScreeningReqResult = action.payload;
          if (action.payload.success === true) {
            state.selectedAppGroupScreening = action.payload.results;
          }
        }
      )
      .addCase(asyncGetApplicationGroupScreening.fulfilled, (state, action) => {
        if (action.payload.success === false) {
        } else {
          state.selectedAppGroupScreening = action.payload;
        }
      })
      .addCase(
        asyncGetApplicationGroupScreeningFromTU.fulfilled,
        (state, action) => {
          if (action.payload.success === false) {
          } else {
            state.selectedAppGroupScreening = action.payload;
          }
        }
      )
      .addCase(asyncGetAllPropertyUnits.fulfilled, (state, action) => {
        state.selectedPropertyUnits = action.payload;
      })
      .addCase(asyncGetPropertyUnits.fulfilled, (state, action) => {
        state.selectedPropertyUnits = action.payload;
      })
      .addCase(asyncGetPropertyUnitRent.fulfilled, (state, action) => {
        state.selectedPropertyUnitRent = action.payload;
      })
      .addCase(asyncUpdateAppInfoService.fulfilled, (state, action) => {
        const { application_group, fieldName, value, unitNumber, property_name } =
          action.payload;

        if (
          state.selectedAppGroup &&
          state.selectedAppGroup.application_group === application_group
        ) {
          if (fieldName === 'ms_property_id') {
            state.selectedAppGroup[fieldName] = value;
            state.selectedAppGroup.desired_unit_number = null;
            state.selectedAppGroup.desired_apartment = null;
          } else if (fieldName === 'desired_apartment') {
            state.selectedAppGroup[fieldName] = value;
            state.selectedAppGroup.desired_unit_number = unitNumber;
          } else {
            state.selectedAppGroup[fieldName] = value;
          }
        }

        state.appGroups = state.appGroups.map((group) =>
          group.application_group === application_group
            ? {
                ...group,
                ...(fieldName === 'ms_property_id'
                  ? {
                    ms_property_id: value,
                    property_name: property_name ?? '',
                    desired_apartment: null,
                    desired_unit_number: null,
                  }
                  : {}),
                ...(fieldName === 'desired_apartment'
                  ? {
                    desired_apartment: value,
                    desired_unit_number: unitNumber
                  }
                  : {}),
              }
            : group
        );
      })
      .addCase(asyncSaveLeaseDraft.fulfilled, (state, action) => {
        if (state.selectedAppGroup) {
          state.selectedAppGroup = {
            ...state.selectedAppGroup,
            leaseDraft: action.payload,
          };
          state.leasePreview = null;
        }
      })
      .addCase(asyncGetTransactionCategories.fulfilled, (state, action) => {
        state.transactionCategories = action.payload?.Categories || [];
      })
      .addCase(asyncGetLeasePreview.fulfilled, (state, action) => {
        state.leasePreview = action.payload;
      })
      .addCase(asyncgetPropertyFeeRecord.fulfilled, (state, action) => {
        state.selectedPropertyFeeRecord = action.payload;
      })
      .addCase(asyncGetAdverseActionPreview.fulfilled, (state, action) => {
        state.adverseActionPdfs = action.payload;
        state.isFetching = false;
        state.error = null;
      })
      .addCase(asyncSendAdverseActionEmails.fulfilled, (state, action) => {
        state.isFetching = false;
        state.error = null;
        state.adverseActionPdfs = null;
      })
  },
});

export const { setSelectedAppGroupId, clearGroupData, clearError, resetEasyAppState } =
  easyapp.actions;
export default easyapp.reducer;